import { FC, useState } from 'react';

import translate from 'common/utils/translate';
import { requestMainRowData } from 'common/utils/facades/story-facade';
import Text from 'admin/components/common/Text';
import { useAdminSelector, useAdminDispatch } from 'admin/reducers';
import type { StoriesListItem } from 'admin/reducers/stories-reducer';
import { UPDATE_STORY } from 'admin/constants/actions';
import { Select } from 'admin/components/common/Form';
import Button from 'admin/components/common/Button';
import { updateStory } from 'admin/actions/story/update-story';
import { selectLoadingStateByName } from 'admin/reducers/loading/selectors';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import { STORY_TYPE } from 'common/constants';
import type { MODAL } from 'admin/constants/common';

export type ChangeStoryTypeModalData = {
	story: StoriesListItem;
};

type Props = ModalManagerProvidedProps<MODAL.CHANGE_STORY_TYPE>;

type StoryType = (typeof STORY_TYPE)[keyof typeof STORY_TYPE];

const t = (key: string, args?: Record<string, string>) => translate(`changeStoryTypeModal.${key}`, args);

const storyTypeOptions = [
	{ value: STORY_TYPE.STANDALONE, label: t('types.standalone') },
	{ value: STORY_TYPE.EMBED, label: t('types.embed') },
	{ value: STORY_TYPE.WIDGET, label: t('types.widget') },
];

const ChangeStoryTypeModal: FC<Props> = ({ open, ...props }) => {
	const dispatch = useAdminDispatch();
	const isBusy = useAdminSelector(state => selectLoadingStateByName(state, UPDATE_STORY.UPDATE));

	const currentType = props.data.story.type ?? STORY_TYPE.STANDALONE;
	const [nextType, setNextType] = useState<StoryType>(currentType);
	const [isComplete, setIsComplete] = useState(false);

	const nextTypeLabel = storyTypeOptions.find(opt => opt.value === nextType)?.label ?? '';

	const onSave = async () => {
		await dispatch(
			updateStory(
				{ ...requestMainRowData(props.data.story), type: nextType },
				{ getStoryOnComplete: false, successAction: 'change-type' }
			)
		);
		setIsComplete(true);
	};

	return (
		<Modal
			title={
				<Text
					size="subtitle"
					weight="semibold"
					text={
						isComplete
							? t('titleComplete', { name: props.data.story.name, type: nextTypeLabel })
							: t('title', { name: props.data.story.name })
					}
				/>
			}
			open={open}
			destroyOnHidden
			width={564}
		>
			<ModalBody>
				{isComplete ? (
					<div style={{ height: 40 }} />
				) : (
					<Select
						label={{ children: t('select.label') }}
						options={storyTypeOptions}
						placeholder={t('select.placeholder')}
						value={nextType}
						eventListeners={{ onChange: setNextType }}
						isSearchable
					/>
				)}
			</ModalBody>
			<ModalFooter>
				{isComplete ? (
					<Button onClick={props.close} view="primary">
						{t('btn.ok')}
					</Button>
				) : (
					<>
						<Button
							loading={isBusy}
							disabled={isBusy || !nextType || currentType === nextType}
							onClick={onSave}
							view="primary"
						>
							{t('btn.save')}
						</Button>
						<Button onClick={props.close} view="secondary">
							{t('btn.cancel')}
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default ChangeStoryTypeModal;
